import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/app/AppShell";
import { FullPageLoading } from "../../components/Loading";
import {
  getProfile,
  listProfiles,
  type FullProfile,
  type ProfileSummary,
} from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { BETA_MODE } from "../../lib/beta";

// Dashboard Basis (Beta) — sticky black Beta banner is handled by <AppShell />.
// Stat tiles read from /me/profiles via listProfiles().

export default function AppDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileSummary | null>(null);
  const [detail, setDetail] = useState<FullProfile | null>(null);
  const [chaptersDone, setChaptersDone] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await listProfiles();
        const p = (r.profiles || [])[0] || null;
        setProfile(p);
        setChaptersDone(p?.chapters_with_content ?? null);
        if (p) {
          try {
            const d = await getProfile(p.phone);
            setDetail(d);
          } catch {
            /* best-effort — greeting falls back to email */
          }
        }
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Fehler beim Laden.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <FullPageLoading />;

  const intervieweeFirstName =
    profile?.interviewee_name?.split(" ")[0] || "—";

  // Greeting: name of the logged-in user. If they are the orderer, use the
  // orderer's first name. Otherwise (a family member), fall back to the
  // email local-part so the greeting stays personal.
  const userEmail = (user?.email || "").toLowerCase();
  const isOrderer =
    !!detail && userEmail && userEmail === (detail.orderer_email || "").toLowerCase();
  const greetingName = isOrderer
    ? (detail?.orderer_first_name || "").trim() ||
      userEmail.split("@")[0] ||
      "Sie"
    : userEmail
      ? userEmail.split("@")[0]
      : "Sie";
  const callsDone = chaptersDone ?? "—";
  const chaptersStarted = chaptersDone ?? "—";
  // 12-week production window; if we have a profile, show remaining weeks
  // based on the chapters done as a coarse proxy.
  const weeksRemaining =
    chaptersDone != null ? Math.max(0, 12 - Math.min(12, chaptersDone)) : "—";

  return (
    <AppShell
      eyebrow="Mein Buch"
      title={
        <>
          Hallo <em>{greetingName}</em>,
        </>
      }
      hideBack
    >
      <div style={{ padding: "0 22px 90px" }}>
        {error && (
          <div
            role="alert"
            style={{
              background: "rgba(107,32,24,0.08)",
              border: "1px solid rgba(107,32,24,0.25)",
              borderLeft: "3px solid var(--oxblood)",
              borderRadius: 4,
              padding: "10px 14px",
              color: "var(--oxblood-d)",
              fontFamily: "var(--f-body)",
              fontSize: 14,
              marginBottom: 18,
            }}
          >
            {error}
          </div>
        )}

        {/* Greeting script + name + storyteller avatar */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <div
            className="oval-frame"
            style={{ width: 40, height: 40, flexShrink: 0, overflow: "hidden" }}
          >
            <img
              src="/images/hildegard-today.jpg"
              alt="Hildegard"
              className="photo-tone"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </div>
          <div
            className="t-script"
            style={{
              fontSize: 26,
              color: "var(--oxblood)",
              lineHeight: 1,
              transform: "rotate(-1deg)",
              display: "inline-block",
            }}
          >
            Hallo {greetingName},
          </div>
        </div>
        <p
          style={{
            fontFamily: "var(--f-display)",
            fontWeight: 400,
            fontSize: 22,
            lineHeight: 1.15,
            color: "var(--ink)",
            margin: "8px 0 22px",
          }}
        >
          {intervieweeFirstName === "—" ? (
            <>Das Buch wächst.</>
          ) : (
            <>
              Das Buch von <em>{intervieweeFirstName}</em> wächst.
            </>
          )}
        </p>

        {/* Stat tiles */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 8,
            marginBottom: 18,
          }}
        >
          <StatTile big={String(callsDone)} small="geführt" label="Anrufe" />
          <StatTile
            big={String(chaptersStarted)}
            small="begonnen"
            label="Kapitel"
          />
          <StatTile
            big={String(weeksRemaining)}
            small="bis Buch"
            label="Wochen"
          />
        </div>

        {/* Current week progress */}
        <ProgressCard chaptersDone={typeof chaptersDone === "number" ? chaptersDone : 0} />

        {/* Locked rows — Beta only */}
        {BETA_MODE && (
          <section style={{ marginTop: 22 }}>
            <div className="t-caps" style={{ marginBottom: 10 }}>
              In Premium enthalten
            </div>
            <LockedRow
              icon="audio"
              name="Hörbuch · in ihrer Stimme"
              sub="Konrads Aufnahmen vertont von einer Erzählstimme"
            />
            <LockedRow
              icon="podcast"
              name="Familien-Podcast"
              sub="Privater RSS-Feed mit allen 12 Episoden"
            />
            <LockedRow
              icon="edit"
              name="Lektorat & Bildbearbeitung"
              sub="Ein menschlicher Lektor liest jedes Kapitel"
            />
          </section>
        )}

        {/* Upgrade teaser — Beta only */}
        {BETA_MODE && (
          <section style={{ marginTop: 18 }}>
            <div
              style={{
                background: "var(--paper-200)",
                border: "1px dashed var(--ochre)",
                borderRadius: 14,
                padding: "16px 18px",
                display: "flex",
                gap: 14,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: "var(--ochre)",
                  color: "var(--paper-50)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontFamily: "var(--f-display)",
                  fontStyle: "italic",
                  fontSize: 20,
                }}
                aria-hidden
              >
                ✦
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontFamily: "var(--f-display)",
                    fontWeight: 500,
                    fontSize: 15,
                    color: "var(--ink)",
                  }}
                >
                  Premium kostenfrei für Beta-Käufer
                </div>
                <div
                  style={{
                    fontFamily: "var(--f-body)",
                    fontStyle: "italic",
                    fontSize: 12.5,
                    color: "var(--ink-soft)",
                    marginTop: 2,
                  }}
                >
                  Wir melden uns, sobald Hörbuch & Podcast verfügbar sind.
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Big CTA */}
        <Link
          to="/app/chapter/1"
          className="btn-primary"
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginTop: 22,
            textDecoration: "none",
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
          </svg>
          Buch lesen
        </Link>
      </div>
    </AppShell>
  );
}

function StatTile({
  big,
  small,
  label,
}: {
  big: string;
  small: string;
  label: string;
}) {
  return (
    <div
      style={{
        background: "var(--paper-50)",
        border: "1px solid rgba(43,26,15,0.10)",
        borderRadius: 12,
        padding: "14px 12px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontFamily: "var(--f-display)",
          fontStyle: "italic",
          fontSize: 30,
          color: "var(--oxblood)",
          lineHeight: 1,
        }}
      >
        {big}
      </div>
      <div
        style={{
          fontFamily: "var(--f-body)",
          fontStyle: "italic",
          fontSize: 11,
          color: "var(--ink-faint)",
          marginTop: 4,
        }}
      >
        {small}
      </div>
      <div
        className="t-caps"
        style={{ fontSize: 9, marginTop: 4 }}
      >
        {label}
      </div>
    </div>
  );
}

function ProgressCard({ chaptersDone }: { chaptersDone: number }) {
  const totalWeeks = 12;
  const currentWeek = Math.min(totalWeeks, Math.max(1, chaptersDone || 1));
  return (
    <>
      <style>{`
        @keyframes vitPulseFill {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.72; }
        }
      `}</style>
      <div
        style={{
          background: "var(--paper-50)",
          border: "1px solid rgba(43,26,15,0.10)",
          borderRadius: 14,
          padding: "16px 18px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 10,
          }}
        >
          <div className="t-caps">Diese Woche</div>
          <div
            style={{
              fontFamily: "var(--f-display)",
              fontStyle: "italic",
              fontSize: 14,
              color: "var(--ink-faint)",
            }}
          >
            Woche {currentWeek} von {totalWeeks}
          </div>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {Array.from({ length: totalWeeks }).map((_, i) => {
            const isDone = i < currentWeek - 1;
            const isCurrent = i === currentWeek - 1;
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  background:
                    isDone || isCurrent
                      ? "var(--oxblood)"
                      : "rgba(43,26,15,0.12)",
                  animation: isCurrent
                    ? "vitPulseFill 1.8s ease-in-out infinite"
                    : undefined,
                }}
              />
            );
          })}
        </div>
      </div>
    </>
  );
}

function LockedRow({
  icon,
  name,
  sub,
}: {
  icon: "audio" | "podcast" | "edit";
  name: string;
  sub: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "12px 14px",
        background: "rgba(43,26,15,0.03)",
        border: "1px dashed rgba(43,26,15,0.20)",
        borderRadius: 12,
        marginBottom: 8,
        opacity: 0.85,
      }}
      aria-disabled="true"
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: "var(--paper-200)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: "var(--ink-faint)",
        }}
      >
        <LockedIcon name={icon} />
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontFamily: "var(--f-display)",
            fontWeight: 500,
            fontSize: 14,
            color: "var(--ink-soft)",
          }}
        >
          {name}
        </div>
        <div
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 12,
            color: "var(--ink-faint)",
          }}
        >
          {sub}
        </div>
      </div>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--ink-faint)"
        strokeWidth="1.8"
        aria-hidden
      >
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 018 0v4" />
      </svg>
    </div>
  );
}

function LockedIcon({ name }: { name: "audio" | "podcast" | "edit" }) {
  const props = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (name === "audio")
    return (
      <svg {...props}>
        <path d="M9 18V5l12-2v13" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="18" cy="16" r="3" />
      </svg>
    );
  if (name === "podcast")
    return (
      <svg {...props}>
        <circle cx="12" cy="12" r="10" />
        <path d="M8 12a4 4 0 018 0M5 12a7 7 0 0114 0" />
      </svg>
    );
  return (
    <svg {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2 2 0 113 3L7 19l-4 1 1-4z" />
    </svg>
  );
}
